import React, { useContext, useState, useEffect } from "react";
import { Avatar, Dropdown, MenuProps, Button, Modal, Divider, Input, message } from "antd";
import { ShoppingOutlined } from "@ant-design/icons";
import { AuthContext } from "../../config/AuthContext";
import { LogoutOutlined, Person, AdminPanelSettings } from "@mui/icons-material";
import { BsBackpack, BsPerson, BsChatDots, BsBook, BsLightbulb, BsShieldCheck } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import HomeColors from "../../styles/HomeColors";
import ChatTableModal from "../jutge/components/ChatTableModal";
import SugerimentsModal from "./SugerimentsModal";
import DualProfileForm from "../metadata/alumnos/DualProfileForm";
import { checkUser } from "../../dao/checkUser";
import { GetAlumneId } from "../../dao/GetAlumneId";
import { GetSceneActivities } from "../../dao/jutge/GetJavamonConfig";

const UserProfileMenu = () => {
    const { isLoggedIn, userPermisos, alumne, user, userId } = useContext(AuthContext);
    const navigate = useNavigate();
    const [historyVisible, setHistoryVisible] = useState(false);
    const [sugerimentsVisible, setSugerimentsVisible] = useState(false);
    const [dualProfileVisible, setDualProfileVisible] = useState(false);

    const [adminSwitchModal, setAdminSwitchModal] = useState(false);
    const [rootPassword, setRootPassword] = useState('');
    const [switchingAdmin, setSwitchingAdmin] = useState(false);
    const isImpersonating = !!localStorage.getItem('adminRestorePoint');

    // Aulas (id) que tienen el juego activo (Joc Actiu = tienen scene activities configuradas).
    const [aulasAmbJoc, setAulasAmbJoc] = useState<number[]>([]);
    useEffect(() => {
        if (!isLoggedIn) return;
        (async () => {
            try {
                const activities = await GetSceneActivities();
                if (Array.isArray(activities) && activities.length > 0) {
                    const uniqueAulas = Array.from(new Set(activities.map((a: any) => a.id_aula)));
                    setAulasAmbJoc(uniqueAulas as number[]);
                }
            } catch (e) {
                // Silencioso: si falla, no se muestran las opciones de juego por joc actiu.
            }
        })();
    }, [isLoggedIn]);

    if (!isLoggedIn) return null;

    const isAdminOrTeacher = user?.tipus === "0" || user?.tipus === "1";

    // Comprobación multi-formato con casting para evitar errores de tipo en tiempo de compilación
    const aulesAlumne = (alumne as any)?.aules || (alumne as any)?.alumne?.aules || [];

    // El usuario tiene acceso al juego si alguna de sus aulas tiene el juego activo y NO está en modo examen (para alumnos)
    const hasExamMode = aulesAlumne.some((a: any) =>
        Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
    );

    const hasGameAccess = aulesAlumne.some((a: any) =>
        Boolean(a.jocHabilitat === 1 || a.jocHabilitat === true || a.joc_habilitat === 1 || a.joc_habilitat === true)
    ) && (!hasExamMode || isAdminOrTeacher);

    // Las opciones de juego del perfil se muestran si tiene acceso al juego, o es admin/profe.
    const canSeeGameMenu = hasGameAccess || isAdminOrTeacher;
    const isDualUser = userPermisos.some((p: any) => p.accio === '/metadata') || 
                      (aulesAlumne && aulesAlumne.some((a: any) => a.dual === 1 || a.is_dual === 1 || a.dual === true));

    const nomAlumne = alumne?.alumne?.nom || "usuari";

    const handleSwitchToAdmin = async () => {
        setSwitchingAdmin(true);
        try {
            const usuariP = await checkUser("root", rootPassword);
            if (usuariP !== 'ERROR' && usuariP.permisos && usuariP.permisos.length > 0) {
                const res = await GetAlumneId(usuariP.permisos[0].id, usuariP.token);
                const currentAlumneInfo = localStorage.getItem('alumneInfo');
                localStorage.removeItem('alumneInfo'); // Libera espacio antes de guardar el backup
                if (currentAlumneInfo) {
                    try {
                        localStorage.setItem('alumneInfo_backup', currentAlumneInfo);
                    } catch (e) {
                        console.warn("No se pudo guardar el alumneInfo_backup por tamaño, se omitirá.", e);
                    }
                }

                // Save current state (without alumneInfo to save space)
                const backup = {
                    token: localStorage.getItem('authToken'),
                    userId: localStorage.getItem('userId'),
                    user: localStorage.getItem('user'),
                    userPermisos: localStorage.getItem('userPermisos')
                };
                localStorage.setItem('adminRestorePoint', JSON.stringify(backup));

                // Set root state in localStorage so AuthContext picks it up on reload
                localStorage.setItem('authToken', usuariP.token);
                localStorage.setItem('userId', String(usuariP.permisos[0].id));
                localStorage.setItem('user', JSON.stringify(usuariP.permisos[0]));
                localStorage.setItem('userPermisos', JSON.stringify(usuariP.permisos));
                if (res) {
                    try {
                        localStorage.setItem('alumneInfo', JSON.stringify(res));
                    } catch (e) {
                        console.warn("No se pudo guardar el alumneInfo de root por tamaño.", e);
                    }
                }

                // force reload to let context pick it up
                window.location.reload();
            } else {
                message.error("Contraseña incorrecta");
            }
        } catch (e) {
            console.error("Error al cambiar a admin:", e);
            message.error("Error al cambiar a admin");
        }
        setSwitchingAdmin(false);
    };

    const handleExitAdmin = () => {
        const backupStr = localStorage.getItem('adminRestorePoint');
        if (backupStr) {
            const backup = JSON.parse(backupStr);
            if (backup.token) localStorage.setItem('authToken', backup.token);
            if (backup.userId) localStorage.setItem('userId', backup.userId);
            if (backup.user) localStorage.setItem('user', backup.user);
            if (backup.userPermisos) localStorage.setItem('userPermisos', backup.userPermisos);
            
            const alumneInfoBackup = localStorage.getItem('alumneInfo_backup');
            if (alumneInfoBackup) {
                try {
                    localStorage.setItem('alumneInfo', alumneInfoBackup);
                } catch (e) {}
                localStorage.removeItem('alumneInfo_backup');
            } else {
                localStorage.removeItem('alumneInfo');
            }

            localStorage.removeItem('adminRestorePoint');
            window.location.reload();
        }
    };

    const profileMenu: MenuProps['items'] = [
        {
            key: "hello",
            label: (
                <div style={{ fontWeight: 500, color: HomeColors.colors.color6 }}>
                    Hola {nomAlumne}!
                </div>
            ),
            disabled: true,
        },
        { type: "divider" },
        {
            key: 'profile',
            label: 'Perfil',
            icon: <BsPerson />,
            onClick: () => navigate('/home/perfil'),
        },
        ...(isDualUser ? [
            {
                key: 'dual',
                label: 'DUAL',
                icon: <BsShieldCheck />,
                onClick: () => setDualProfileVisible(true),
            }
        ] : []),
        {
            key: 'converses',
            label: 'Converses',
            icon: <BsChatDots />,
            onClick: () => setHistoryVisible(true),
        },
        ...(user?.tipus === "1" && !isImpersonating ? [
            {
                key: 'cambiarAdmin',
                label: 'Cambiar a Admin',
                icon: <AdminPanelSettings style={{ color: '#faad14' }} />,
                onClick: () => { setRootPassword(''); setAdminSwitchModal(true); },
            }
        ] : []),
        ...(isImpersonating ? [
            {
                key: 'salirAdmin',
                label: 'Salir de Admin',
                icon: <LogoutOutlined style={{ color: 'red' }} />,
                onClick: handleExitAdmin,
            }
        ] : []),
        ...(canSeeGameMenu ? [
            {
                key: 'inventari',
                label: 'Inventari',
                icon: <BsBackpack />,
                onClick: () => navigate('/home/mochila'),
            },
            {
                key: 'javadex',
                label: 'JAVAdex',
                icon: <BsBook />,
                onClick: () => navigate('/home/javadex'),
            },
            {
                key: 'javashop',
                label: 'JAVATienda',
                icon: <ShoppingOutlined />,
                onClick: () => navigate('/home/javashop'),
            },
            {
                key: 'sugerencia',
                label: 'Sugerencia',
                icon: <BsLightbulb />,
                onClick: () => setSugerimentsVisible(true),
            }
        ] : []),
        {
            key: 'logout',
            label: 'Sortir',
            icon: <LogoutOutlined />,
            onClick: () => {
               localStorage.clear();
               navigate('/');
               window.location.reload();
            },
        }
    ];

    const avatarSrc = (() => {
        let img = alumne?.alumne?.avatar;
        if (!img || img === "undefined" || img === "null") return undefined;
        if (img === "avatar1.jpg") return "/images/avatar1.jpg";
        if (img.startsWith("http://localhost:") && window.location.hostname !== 'localhost') {
            img = img.replace(/http:\/\/localhost:\d+/, `${process.env.REACT_APP_API_URL}:8061`);
        }
        if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
        if (img.endsWith(".jpg") || img.endsWith(".png") || img.endsWith(".jpeg") || img.endsWith(".svg")) return `/images/${img}`;
        if (img.startsWith("base64:")) img = img.split(":").pop()!;
        if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
        if (img.startsWith("iVB")) return `data:image/png;base64,${img}`;
        return `data:image/png;base64,${img}`;
    })();

    return (
        <>
            <Dropdown menu={{ items: profileMenu }} placement="bottomRight" trigger={['click']}>
                <Avatar
                    src={avatarSrc}
                    style={{ cursor: "pointer", backgroundColor: HomeColors.colors.color5, width: 40, height: 40 }}
                    icon={<Person />}
                />
            </Dropdown>

            <ChatTableModal
                visible={historyVisible}
                onClose={() => setHistoryVisible(false)}
                isHistory={true}
                aula={{ id: 0 }}
            />
            
            <SugerimentsModal visible={sugerimentsVisible} onClose={() => setSugerimentsVisible(false)} />

            <Modal
                title="Configuració DUAL Alumne"
                open={dualProfileVisible}
                onCancel={() => setDualProfileVisible(false)}
                footer={null}
                width={800}
                centered
            >
                <div style={{ padding: '10px' }}>
                <DualProfileForm 
                    idUsuari={user?.id || 0} 
                    onSuccess={() => setDualProfileVisible(false)} 
                />
            </div>
        </Modal>

        <Modal
            title="Cambiar a Admin"
            open={adminSwitchModal}
            onCancel={() => setAdminSwitchModal(false)}
            onOk={handleSwitchToAdmin}
            confirmLoading={switchingAdmin}
        >
            <p>Introduce la contraseña del usuario root:</p>
            <Input.Password
                autoFocus
                value={rootPassword}
                onChange={(e) => setRootPassword(e.target.value)}
                onPressEnter={handleSwitchToAdmin}
                placeholder="Contraseña de root"
            />
        </Modal>
    </>
);
};

export default UserProfileMenu;
